/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: ba-ui-common (C) Copyright IBM Corp. 2019, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import PropTypes from 'prop-types';

import React, { Component } from 'react';
import { FlexLayout, FlexItem, Label } from '@waca/ca-ui-toolkit';
import stringResource from 'ba-filter-nls';

import HierarchyFilterPane from './HierarchyFilterPane';
import FilterHeader from './FilterHeader';
import FilterFooter from './FilterFooter';

export default class HierarchyFilter extends Component<any, any> {
	static propTypes = {
		title: PropTypes.string,
		subTitle: PropTypes.string,
		onClose: PropTypes.func.isRequired,
		onSearch: PropTypes.func.isRequired,
		onDoneSearch: PropTypes.func,
		onClear: PropTypes.func.isRequired,
		onApply: PropTypes.func.isRequired,
		onDone: PropTypes.func.isRequired,
		loading: PropTypes.bool,
		allowSearch: PropTypes.bool,
		isDirty: PropTypes.bool,
		isSearching: PropTypes.bool,
		// tree
		getValue: PropTypes.func.isRequired,
		getNumberOfChildren: PropTypes.func.isRequired,
		onExpandCollapse: PropTypes.func.isRequired,
		onClick: PropTypes.func.isRequired,
		contentRenderer: PropTypes.func,
		limitChildren: PropTypes.number,
		onInvert: PropTypes.func.isRequired,
		selectedValuesCount: PropTypes.number,
		// list
		listOnClick: PropTypes.func,
		listGetValue: PropTypes.func,
		listGetNumberOfChildren: PropTypes.func,
		listContentRenderer: PropTypes.func,
		listOnInvert: PropTypes.func,
		showSubcaption: PropTypes.bool.isRequired,
		readOnly: PropTypes.bool,
	};

	// Define default property values
	static defaultProps = {
		title: stringResource.get('filter'),
		subTitle: stringResource.get('useCheckboxes'),
		loading: false,
		readOnly: false,
		isSearching: false,
	};

	state = {
		showSearchInput: true,
		isDirty: false,
		// these states are used to determine dirty
		// these values are set in _resetDirty()
	};

	componentWillMount() {
		this._resetDirty();
	}

	componentWillReceiveProps() {
		// We have received some props so mark the dirty flag to true, note this is
		// not called during the initial render
		if (!this.state.isDirty) {
			this.setState({ isDirty: true });
		}
	}

	_resetDirty = () => {
		this.setState({
			isDirty: false,
		});
	};

	_onSearchClick = () => {
		this.setState(prevState => ({ showSearchInput: !prevState.showSearchInput }));
	};

	_onApply = () => {
		this.props.onApply();
		this._resetDirty();
	};

	_onClear = () => {
		this.props.onClear();
	};

	_onOk = () => {
		this._onApply();
		this.props.onDone();
	};

	_onCancel = () => {
		this.props.onClose();
	};

	render() {
		const {
			title,
			subTitle,
			onClose,
			onSearch,
			onDoneSearch,
			getValue,
			getNumberOfChildren,
			onExpandCollapse,
			onClick,
			limitChildren,
			contentRenderer,
			onInvert,
			selectedValuesCount,
			listOnClick,
			listGetValue,
			listGetNumberOfChildren,
			listContentRenderer,
			listOnInvert,
			showSubcaption,
			loading,
			readOnly,
			allowSearch,
			isSearching,
		} = this.props;
		const { showSearchInput } = this.state;

		return (
			<FlexLayout fullHeight direction="column" data-tid="hierarchyFilter">
				<FlexItem gutter={3}>
					<FilterHeader
						title={title}
						showSearch={false} // always false, this controls the icon
						onSearch={this._onSearchClick}
						showMenu={false}
						showClose
						onClose={onClose}
						loading={loading}
						height={16}
					/>
				</FlexItem>
				<FlexItem margin={[0, 3, 0, 3]}>
					<Label label={subTitle} />
				</FlexItem>
				<FlexItem grow margin={3}>
					<HierarchyFilterPane
						showSearch={showSearchInput}
						allowSearch={allowSearch}
						onSearch={onSearch}
						onDoneSearch={onDoneSearch}
						loading={loading}
						isSearching={isSearching}
						// tree
						getValue={getValue}
						getNumberOfChildren={getNumberOfChildren}
						onExpandCollapse={onExpandCollapse}
						onClick={onClick}
						contentRenderer={contentRenderer}
						limitChildren={limitChildren}
						onInvert={onInvert}
						selectedValuesCount={selectedValuesCount}
						// list
						listOnClick={listOnClick}
						listGetValue={listGetValue}
						listGetNumberOfChildren={listGetNumberOfChildren}
						listOnInvert={listOnInvert}
						listContentRenderer={listContentRenderer}
						showSubcaption={showSubcaption}
						readOnly={readOnly}
						onClear={this._onClear}
					/>
				</FlexItem>

				<FlexItem gutter={[0, 3, 3, 3]}>
					<FilterFooter
						loading={loading}
						readOnly={readOnly}
						onClear={this._onClear}
						onApply={this._onApply}
						onOk={this._onOk}
						onCancel={this._onCancel}
						dirty={
							this.props.isDirty === true || this.props.isDirty === false
								? this.props.isDirty
								: this.state.isDirty
						}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}
}
